import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import PhotoViewer from "./PhotoViewer";
import MapPickDialog from "./MapPickDialog";
import IdentifyPlace from "./IdentifyPlace";
import { reverseGeocodeCoords, findIdentifyCandidates } from "../../Common/placeIdentify";
import { placesToIdentify, placePhotos, applyPlaceRenames } from "../../Common/identifyPlaces";
import { isFileLikeName } from "../../Common/placeNaming";
import { isWebPhoto } from "../../Common/photoSource";
import { PLACE_RENAMED_EVENT } from "./RenameFollowUpModal";

// Places menu → "Identify Places" (4.10.55, 2026-10-28): like Personalize
// Guide with Photos' review, but for the places already in the guide — no
// photo grouping, just naming each place from its location (nearby
// landmarks, the city, or a spot picked on the map). Photos that aren't the
// person's own are labelled "Web photo" (Common/photoSource.js). Nothing
// changes until "Rename"; renamed places' events follow and the itinerary
// question is asked once for all of them (RenameFollowUpModal).
const THUMB_W = 90;
const THUMB_H = 80;

function resolveUrl(url) {
  if (!url) return null;
  if (url.startsWith("gdrive:")) return `https://lh3.googleusercontent.com/d/${url.slice(7)}`;
  if (url.startsWith("http")) return url;
  return `http://liquidguides.com//Media/Photos/${url}`;
}

function Thumb({ url, name, onEnlarge }) {
  const web = isWebPhoto(url);
  return (
    <div className="ipl-thumb" style={{ borderRadius: 8, background: "#f3f4f6", overflow: "hidden", width: THUMB_W, flexShrink: 0, position: "relative" }} title={name}>
      <img src={resolveUrl(url)} alt="" referrerPolicy="no-referrer" style={{ width: THUMB_W, height: THUMB_H, objectFit: "cover", display: "block" }} />
      {web ? (
        <div className="ipl-web" style={{ position: "absolute", top: 0, left: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, padding: "2px 4px", textAlign: "center" }}>
          Web photo
        </div>
      ) : null}
      <button
        className="pjr-enlarge"
        onClick={onEnlarge}
        title="See this photo full size"
        aria-label="See this photo full size"
        style={{ position: "absolute", bottom: 2, left: 2, border: "none", borderRadius: 4, background: "rgba(0,0,0,0.55)", cursor: "pointer", color: "#fff", fontSize: 11, padding: "2px 4px" }}
      >
        🔍
      </button>
    </div>
  );
}

export default function IdentifyPlacesModal({ show, onHide, onApplied }) {
  const [places, setPlaces] = useState([]);
  const [names, setNames] = useState({}); // place ID → name typed or picked
  const [identify, setIdentify] = useState({}); // place ID → IdentifyPlace state
  const [onlyFileNames, setOnlyFileNames] = useState(true);
  const [viewer, setViewer] = useState(null); // { url, name }
  const [mapPick, setMapPick] = useState(null); // { id, lat, lng }
  const [bulk, setBulk] = useState(null); // { done, total } while identifying all

  // Fresh list each time the dialog opens.
  const [wasShown, setWasShown] = useState(false);
  if (show !== wasShown) {
    setWasShown(show);
    if (show) {
      const list = placesToIdentify(window.guide_obj);
      setPlaces(list);
      setNames({});
      setIdentify({});
      setOnlyFileNames(list.some((p) => isFileLikeName(p.Name)));
    }
  }

  const fileNamed = places.filter((p) => isFileLikeName(p.Name));
  const shown = onlyFileNames && fileNamed.length > 0 ? fileNamed : places;
  const nameOf = (p) => (names[p.ID] !== undefined ? names[p.ID] : p.Name);
  const changed = places.filter((p) => String(nameOf(p)).trim() && String(nameOf(p)).trim() !== String(p.Name).trim());

  const run = async (p) => {
    setIdentify((prev) => ({ ...prev, [p.ID]: { status: "loading" } }));
    const lat = parseFloat(p.Lat);
    const lng = parseFloat(p.Longi);
    const [{ city }, candidates] = await Promise.all([reverseGeocodeCoords(lat, lng), findIdentifyCandidates(lat, lng)]);
    setIdentify((prev) => ({ ...prev, [p.ID]: { status: "done", city, candidates, pickedTitle: null } }));
  };

  // One at a time, like Personalize's "Identify Clusters" (each is a billed
  // reverse-geocode call); places already looked up are skipped.
  const runAll = async () => {
    const todo = shown.filter((p) => !identify[p.ID] || identify[p.ID].status !== "done");
    setBulk({ done: 0, total: todo.length });
    for (let i = 0; i < todo.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await run(todo[i]);
      setBulk({ done: i + 1, total: todo.length });
    }
    setBulk(null);
  };

  const pick = (p, title) => {
    setIdentify((prev) => ({ ...prev, [p.ID]: { ...prev[p.ID], pickedTitle: title } }));
    setNames((prev) => {
      const next = { ...prev };
      if (title) next[p.ID] = title;
      else delete next[p.ID]; // "None of these": keep the current name
      return next;
    });
  };

  const cancelOne = (p) => {
    setIdentify((prev) => {
      const next = { ...prev };
      delete next[p.ID];
      return next;
    });
    setNames((prev) => {
      const next = { ...prev };
      delete next[p.ID];
      return next;
    });
  };

  const apply = () => {
    const done = applyPlaceRenames(window.guide_obj, changed.map((p) => ({ id: p.ID, newName: nameOf(p) })));
    if (done.length > 0) window.dispatchEvent(new CustomEvent(PLACE_RENAMED_EVENT, { detail: { renames: done } }));
    if (onApplied) onApplied(done);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable className="identify-places-modal">
      {mapPick ? (
        <MapPickDialog
          show
          lat={mapPick.lat}
          lng={mapPick.lng}
          onHide={() => setMapPick(null)}
          onPick={(name) => setNames((prev) => ({ ...prev, [mapPick.id]: name }))}
        />
      ) : null}
      {viewer ? <PhotoViewer photos={[{ src: resolveUrl(viewer.url), name: viewer.name }]} index={0} onClose={() => setViewer(null)} /> : null}
      <Modal.Header closeButton style={{ padding: "12px 16px" }}>
        <div style={{ width: "100%" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
            Identify Places — {shown.length} place{shown.length !== 1 ? "s" : ""}
          </Modal.Title>
          {bulk ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <FontAwesomeIcon icon={solid("spinner")} spin style={{ color: "#6b7280", fontSize: 12 }} />
              <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                Identifying… {bulk.done}/{bulk.total}
              </span>
              <div style={{ flex: 1, height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${bulk.total ? (bulk.done / bulk.total) * 100 : 0}%`, background: "#6366f1", transition: "width 0.2s" }} />
              </div>
            </div>
          ) : null}
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "8px 16px" }}>
        <p style={{ fontSize: "0.8rem", color: "#666", margin: "0 0 8px" }}>
          Name each place from where it is: 🔎 Identify shows nearby landmarks and the city, or pick the spot on the map,
          or type a name. 🔍 shows a photo full size; "Web photo" marks photos that aren't yours. Nothing changes until
          you click Rename.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          {fileNamed.length > 0 && fileNamed.length < places.length ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                id="ipl-only-file-names"
                checked={onlyFileNames}
                onChange={(e) => setOnlyFileNames(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <label htmlFor="ipl-only-file-names" style={{ fontSize: "0.8rem", color: "#374151", cursor: "pointer", margin: 0 }}>
                Only places still named after a photo file ({fileNamed.length} of {places.length})
              </label>
            </div>
          ) : null}
          {shown.length > 0 ? (
            <button
              className="ipl-identify-all"
              onClick={runAll}
              disabled={Boolean(bulk)}
              title="Look up nearby landmarks and the city for every place below"
              style={{ marginLeft: "auto", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#374151", fontSize: "0.75rem", padding: "4px 10px", cursor: bulk ? "default" : "pointer", whiteSpace: "nowrap" }}
            >
              {bulk ? `Identifying… ${bulk.done}/${bulk.total}` : "🔎 Identify all"}
            </button>
          ) : null}
        </div>
        {shown.length === 0 ? <p style={{ fontSize: 13, color: "#6b7280" }}>No places with a location in this guide.</p> : null}

        {shown.map((p) => {
          const photos = placePhotos(p);
          return (
            <div key={p.ID} className="ipl-row" style={{ padding: "10px 4px", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  className="pjr-name-input ipl-name"
                  value={nameOf(p)}
                  onChange={(e) => setNames((prev) => ({ ...prev, [p.ID]: e.target.value }))}
                  aria-label={`Name for ${p.Name}`}
                  style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", border: "1px solid #d1d5db", borderRadius: 6, padding: "3px 8px", minWidth: 0, width: 280, maxWidth: "60%" }}
                />
                {String(nameOf(p)).trim() !== String(p.Name).trim() ? (
                  <span style={{ fontSize: 11, color: "#6b7280", overflowWrap: "anywhere" }}>was {p.Name}</span>
                ) : p.City ? (
                  <span style={{ fontSize: 11, color: "#6b7280" }}>{p.City}</span>
                ) : null}
              </div>
              {photos.length > 0 ? (
                <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                  {photos.map((url) => (
                    <Thumb key={url} url={url} name={p.Name} onEnlarge={() => setViewer({ url, name: p.Name })} />
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: "#9ca3af" }}>No photo</div>
              )}
              <IdentifyPlace
                state={identify[p.ID]}
                onRun={() => run(p)}
                onFindCandidates={() => run(p)}
                onPick={(title) => pick(p, title)}
                onPickOnMap={() => setMapPick({ id: p.ID, lat: parseFloat(p.Lat), lng: parseFloat(p.Longi) })}
                onCancel={identify[p.ID] ? () => cancelOne(p) : null}
              />
            </div>
          );
        })}
      </Modal.Body>

      <Modal.Footer style={{ padding: "10px 16px", gap: 8 }}>
        <button className="btn btn-secondary btn-sm" onClick={onHide}>
          Cancel
        </button>
        <button className="btn btn-primary btn-sm ipl-apply" onClick={apply} disabled={changed.length === 0}>
          Rename {changed.length} place{changed.length !== 1 ? "s" : ""}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
